import React, { useEffect, useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { atsFetch } from './api';
import OutcomePrompts from './OutcomePrompts';

// ATS/CRM section shell. Group order per Alex: Workspace first (the daily
// desk), then ATS (delivery), then CRM (business development).
// Workspace mode: 'recruitment' shows everything; 'saas' hides the
// recruitment-specific ATS group (Alex dogfooding MarketMapr as his own
// company CRM — clients, contacts, deals with MRR).
const GROUPS: { label: string | null; recruitmentOnly?: boolean; tabs: { to: string; label: string; end?: boolean }[] }[] = [
  { label: null, tabs: [{ to: '/ats', label: 'Overview', end: true }, { to: '/ats/reports', label: 'Reports' }] },
  {
    label: 'Workspace',
    tabs: [
      { to: '/ats/inbox', label: 'Inbox' },
      { to: '/ats/tasks', label: 'Tasks' },
      { to: '/ats/notes', label: 'Notes' },
      { to: '/ats/calendar', label: 'Calendar' },
    ],
  },
  {
    label: 'ATS',
    recruitmentOnly: true,
    tabs: [
      { to: '/ats/candidates', label: 'Candidates' },
      { to: '/ats/roles', label: 'Roles' },
      { to: '/ats/placements', label: 'Placements' },
    ],
  },
  {
    label: 'CRM',
    tabs: [
      { to: '/ats/clients', label: 'Clients' },
      { to: '/ats/contacts', label: 'Contacts' },
      { to: '/ats/deals', label: 'Deals' },
    ],
  },
];

const AtsLayout: React.FC = () => {
  const { token } = useAuth();
  const [mode, setMode] = useState<'recruitment' | 'saas'>('recruitment');

  useEffect(() => {
    if (!token) return;
    atsFetch(token, '/settings').then((d) => setMode(d.mode === 'saas' ? 'saas' : 'recruitment')).catch(() => {});
  }, [token]);

  const changeMode = async (m: 'recruitment' | 'saas') => {
    setMode(m);
    if (token) atsFetch(token, '/settings', { method: 'PUT', body: JSON.stringify({ mode: m }) }).catch(() => {});
  };

  const groups = GROUPS.filter((g) => !(g.recruitmentOnly && mode === 'saas'));

  return (
    <div className="max-w-7xl mx-auto px-6 py-6">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-semibold text-gray-900">ATS / CRM</h1>
        <label className="flex items-center gap-2 text-xs text-gray-400">
          Workspace mode
          <select
            value={mode}
            onChange={(e) => changeMode(e.target.value as 'recruitment' | 'saas')}
            className="border border-gray-200 rounded-md px-2 py-1 text-xs text-gray-600"
            title="SaaS mode hides the recruitment-specific ATS tabs and runs the CRM around clients, contacts and deals"
          >
            <option value="recruitment">Recruitment</option>
            <option value="saas">SaaS / B2B</option>
          </select>
        </label>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        {mode === 'saas'
          ? 'Clients, contacts and deals — your company CRM.'
          : "Delivery on the ATS side, business development on the CRM side — the records you're actively working."}
      </p>
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex flex-wrap items-end gap-x-1 -mb-px">
          {groups.map((g, gi) => (
            <React.Fragment key={g.label || 'root'}>
              {gi > 0 && <span className="mx-2 mb-2.5 h-5 w-px bg-gray-200 self-center" />}
              {g.label && (
                <span className="mb-3 mr-1 text-[9px] font-bold uppercase tracking-widest text-gray-300 self-center">{g.label}</span>
              )}
              {g.tabs.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.end as any}
                  className={({ isActive }) =>
                    `px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      isActive
                        ? 'border-emerald-600 text-emerald-700'
                        : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
                    }`
                  }
                >
                  {t.label}
                </NavLink>
              ))}
            </React.Fragment>
          ))}
        </nav>
      </div>
      <Outlet />
      <OutcomePrompts />
    </div>
  );
};

export default AtsLayout;
